<template>
  <div v-on-click-outside="() => (show = false)" class="relative">
    <div
      class="relative flex h-10 cursor-pointer items-center justify-between rounded-sm border px-3 py-2 whitespace-nowrap"
      v-bind="$attrs"
      @click="show = !show"
    >
      <div v-if="modelValue" class="flex items-center space-x-2 text-gray-500">
        <icon-base
          height="18"
          width="16"
          :icon="'risk-tier-' + modelValue.indicator"
        />
        <span>{{ modelValue.label }}</span>
      </div>
      <span v-else>
        {{ $t("SCORECARDS.RISK_TIER") }}
      </span>

      <icon-base
        :class="{ 'rotate-180 transform': show }"
        height="6"
        width="10"
        :icon="IconDropdownArrow"
        :icon-color="UTIL_COLORS.DEFAULT"
      />
    </div>

    <ul
      v-if="show"
      class="absolute left-0 w-full rounded-sm bg-white shadow-sm"
    >
      <li
        v-for="riskTier in options"
        :key="riskTier.label"
        class="selector-dd-list-item"
        @click.stop="handleItemClick(riskTier)"
      >
        <icon-base
          class="ml-2-5"
          height="18"
          width="16"
          :icon="'risk-tier-' + riskTier.indicator"
        />
        <span>{{ riskTier.label }}</span>
      </li>
      <li
        class="selector-dd-list-item border-t"
        @click.stop="handleItemClick()"
      >
        <icon-base height="16" width="16" :icon="IconGear" class="mx-1" />
        <span>
          {{ $t("SCORECARDS.CREATE_CUSTOM_RISK_TIER") }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconGear from "@/components/icons/IconGear.vue";
import { UTIL_COLORS } from "@/helpers/constants";
import { useRiskTiers } from "@/hooks/options";
import { useScorecards } from "@/hooks/scorecards";

import type { IRiskTierDropdownItem } from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits(["update:modelValue", "customSelected"]);

defineProps({
  modelValue: {
    type: Object as PropType<IRiskTierDropdownItem | null>,
  },
});

const { activeScorecard, activeScorecardGroup, isScorecardPage } =
  useScorecards(false);

const { riskTiersPiorityMap } = useRiskTiers();

const options = computed(() => {
  const riskTiers =
    (isScorecardPage.value
      ? activeScorecard.value?.risk_tiers
      : activeScorecardGroup.value?.risk_tiers) || [];
  return riskTiers
    .map((rt) => ({
      label: rt.name,
      indicator: rt.indicator,
    }))
    .sort(
      (a, b) =>
        (riskTiersPiorityMap.value[a.label] ?? 0) -
        (riskTiersPiorityMap.value[b.label] ?? 0)
    );
});

const show = ref(false);

const handleItemClick = (item?: IRiskTierDropdownItem) => {
  if (!item) {
    emit("customSelected");
    show.value = false;
    return;
  }
  emit("update:modelValue", item);
  show.value = false;
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.selector-dd-list-item {
  @apply flex cursor-pointer items-center space-x-2 px-2 py-2 whitespace-nowrap text-gray-500 hover:bg-gray-200;
}
</style>
